import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import reconciliationService from '@api/v1/reconciliationService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

const BankReconciliation = () => {
  useDocumentTitle('Bank Reconciliation')
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [activeTab, setActiveTab] = useState('unreconciled')

  /*
   * CC-53132 — four reads on this page, and each one of them used to be able to
   * report "nothing here" about a request that was never sent.
   *
   * react-query's `isLoading` is `isPending && isFetching`, so it is FALSE for a
   * query that is pending but NOT in flight. The dominant way that happens is a
   * PAUSED fetch: under the default `networkMode: 'online'` react-query refuses to
   * fire while `onlineManager` believes the browser is offline. `queryFn` is never
   * called, nothing throws, nothing is logged, and `data` stays `undefined` — which
   * every derived value below then collapsed to `[]` / `0`.
   *
   * On THIS page that rendered as "No reconciliation accounts found", "All
   * transactions reconciled" and "No reconciliation history". Those are three
   * sentences a bookkeeper closing a bank month acts on: the first says the firm has
   * nothing to reconcile, the second says the account is already clean, the third
   * says nobody has ever reconciled it. All three were indistinguishable from a read
   * that never happened, with no spinner and no error to hint otherwise.
   *
   * So every render site below switches on `readState` instead of `isLoading`, and
   * the derived arrays are consumed ONLY inside the `ready` branch. `useReadState`
   * rather than the bare `readState` because it carries the permanent blocked-read
   * diagnostic — a paused fetch leaves no trace on the client OR the server by
   * construction, so that log is the only evidence anyone will ever get.
   */
  const settingsQuery = useQuery({
    queryKey: ['recon-settings'],
    queryFn: () => reconciliationService.getSettings(),
  })
  const settingsState = useReadState('recon-settings', settingsQuery)
  const { data: settingsData, error } = settingsQuery

  const unreconQuery = useQuery({
    queryKey: ['recon-unreconciled', selectedAccountId],
    queryFn: () => reconciliationService.getUnreconciled(selectedAccountId),
    enabled: !!selectedAccountId && activeTab === 'unreconciled',
  })
  const unreconState = useReadState('recon-unreconciled', unreconQuery)
  const { data: unreconData } = unreconQuery

  const resultId = unreconData?.data?.resultId
  const rowCount = unreconData?.data?.rowCount || 0

  const txQuery = useQuery({
    queryKey: ['recon-transactions', selectedAccountId, resultId],
    queryFn: () => reconciliationService.getTransactions(selectedAccountId, { resultId, limit: 100 }),
    enabled: !!selectedAccountId && !!resultId && rowCount > 0 && activeTab === 'unreconciled',
  })
  const txState = useReadState('recon-transactions', txQuery)
  const { data: txData } = txQuery

  const historyQuery = useQuery({
    queryKey: ['recon-history', selectedAccountId],
    queryFn: () => reconciliationService.getHistory(selectedAccountId),
    enabled: !!selectedAccountId && activeTab === 'history',
  })
  const historyState = useReadState('recon-history', historyQuery)
  const { data: historyData } = historyQuery

  /*
   * The `[]` / `|| 0` fallbacks below are deliberately KEPT rather than replaced
   * with `undefined`, because every one of them is now read only inside its
   * query's `ready` branch — the state check, not the value, is what decides
   * whether an absence may be described to the user. Written down because the
   * fallback on its own is the exact idiom this ticket is about, and the next
   * reader needs to know it is load-bearing for shape normalisation only.
   *
   * `rowCount` is the one exception that is read while unresolved: it gates
   * `txQuery`'s `enabled`. That is correct — an unresolved parent read must leave
   * the child read UNASKED, not asked with a made-up count.
   */
  const settings = settingsData?.data
  const accounts = Array.isArray(settings) ? settings : (settings?.settings || settings?.accounts || settings?.Accounts || [])
  const unrecon = unreconData?.data
  const history = Array.isArray(historyData?.data) ? historyData.data : []

  const TABS = [
    { id: 'unreconciled', label: 'Unreconciled' },
    { id: 'history', label: 'History' },
  ]

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Bank Reconciliation</h1>
        <p className="text-sm text-gray-500 mt-1">Reconcile bank accounts and review transaction history</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Account List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto" role="region" aria-label="Bank accounts">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Bank Accounts</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {/* `idle` rides with `loading` on purpose: this query carries no
                `enabled` option, so it cannot reach that state today. It is folded
                in anyway because every branch here is gated on a NAMED state, and
                an unhandled fifth state falls through all of them and renders
                nothing — a blank panel, which is what whoever adds an `enabled`
                gate here later would silently get. */}
            {settingsState === 'loading' || settingsState === 'idle' ? (
              <div className="p-8 flex justify-center"><LoadingSpinner message="Loading accounts..." /></div>
            ) : settingsState === 'blocked' ? (
              /* The state that used to render as "No reconciliation accounts
                 found". No request was sent, so this panel knows nothing at all
                 about the firm's bank accounts — and a reconciler who reads "none"
                 stops looking rather than retrying. A spinner forever would be
                 nearly as bad, so say what actually happened and give a way out. */
              <div role="alert" className="p-5 text-sm text-amber-900 bg-amber-50">
                <p className="font-medium">Can’t reach the server, so we haven’t loaded your bank accounts.</p>
                <p className="mt-1 text-amber-800">
                  This is not a firm with no reconciliation accounts — your connection looks
                  offline, so the list was never read. Check it and try again.
                </p>
                <button
                  type="button"
                  onClick={() => settingsQuery.refetch()}
                  className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Try again
                </button>
              </div>
            ) : settingsState === 'failed' ? (
              /* Kept the original sentence — it was already correct and already
                 announced. What it lacked was the second half: a reader who sees a
                 red line above an account list still has no way to tell whether the
                 list is empty or unread, and no way to ask again short of reloading
                 the whole app. */
              <div role="alert" className="p-5 text-sm text-red-700 bg-red-50">
                <p className="font-medium">Failed to load accounts</p>
                <p className="mt-1">
                  This is not a firm with no reconciliation accounts — nothing was read.
                  {error?.message ? ` ${error.message}` : ''}
                </p>
                <button
                  type="button"
                  onClick={() => settingsQuery.refetch()}
                  className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Try again
                </button>
              </div>
            ) : accounts.length === 0 ? (
              <div className="p-5">
                <p className="text-sm text-gray-500 text-center mb-3">No reconciliation accounts found</p>
                {settings && (
                  <pre className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 overflow-x-auto max-h-48">
                    {JSON.stringify(settings, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              accounts.map((acct, i) => {
                const id = acct.AccountId || acct.Id || acct.id || i
                const name = acct.AccountName || acct.Name || acct.name || `Account ${i + 1}`
                return (
                  <button
                    key={id}
                    onClick={() => setSelectedAccountId(id)}
                    className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-cb-accent/30 ${
                      selectedAccountId === id ? 'bg-cb-light border-l-2 border-cb-accent' : ''
                    }`}
                    aria-selected={selectedAccountId === id}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{name}</p>
                      {acct.IsSaveForLater && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">In Progress</span>
                      )}
                    </div>
                    {acct.AccountTypeName && (
                      <p className="text-xs text-gray-500 mt-0.5">{acct.AccountTypeName} {acct.CenterbaseAccountNumber ? `#${acct.CenterbaseAccountNumber}` : ''}</p>
                    )}
                    {acct.StatementDate && (
                      <p className="text-xs text-gray-400 mt-0.5">Last: {acct.StatementDate?.split('T')[0]}</p>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Reconciliation Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100" role="region" aria-label="Reconciliation">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {selectedAccountId ? 'Reconciliation' : 'Select an Account'}
            </h2>
            {selectedAccountId && (
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" role="tablist">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!selectedAccountId ? (
            <p className="p-8 text-sm text-gray-500 text-center">Select a bank account to begin reconciliation</p>
          ) : activeTab === 'unreconciled' ? (
            <div className="p-5" role="tabpanel" aria-label="Unreconciled transactions">
              {unreconState === 'loading' ? (
                <div className="py-8 flex justify-center"><LoadingSpinner message="Loading unreconciled..." /></div>
              ) : unreconState === 'blocked' ? (
                /* "All transactions reconciled" is the single most dangerous
                   sentence on this page — it is a green tick over an account whose
                   unreconciled list was never fetched. A paused read produced it
                   with no spinner and no error, so nothing on screen or in any log
                   contradicted it. */
                <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                  <p className="font-medium">Can’t reach the server, so we haven’t checked this account.</p>
                  <p className="mt-1 text-amber-800">
                    This does not mean the account is reconciled — your connection looks offline,
                    so its unreconciled transactions were never read. Check it and try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => unreconQuery.refetch()}
                    className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              ) : unreconState === 'failed' ? (
                /* This read's `error` had no render site at all before: only
                   `isLoading` and the data were destructured, so a thrown read fell
                   through to the "No data" line below — which reads as an answer. */
                <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                  <p className="font-medium">We couldn’t check this account: {unreconQuery.error?.message}</p>
                  <p className="mt-1">
                    This does not mean the account is reconciled — nothing was read.
                  </p>
                  <button
                    type="button"
                    onClick={() => unreconQuery.refetch()}
                    className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Try again
                  </button>
                </div>
              ) : unreconState === 'idle' ? (
                /* This query IS conditionally enabled, so `idle` is the ordinary
                   "not asked yet" state and must NOT be dressed as an error or as a
                   spinner (a spinner here would never stop). It is structurally
                   unreachable today — the `enabled` condition is exactly the pair of
                   conditions under which this panel renders — but "unreachable"
                   is a property of the JSX around it, not of the query, and the
                   catalog fix already shipped one blank page by assuming a state
                   could not happen. */
                <p className="text-sm text-gray-500 text-center py-4">
                  This account’s unreconciled transactions haven’t been requested yet.
                </p>
              ) : !unrecon ? (
                <p className="text-sm text-gray-500 text-center py-4">No data</p>
              ) : rowCount === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-green-700 font-medium">All transactions reconciled</p>
                  <p className="text-xs text-gray-500 mt-1">No unreconciled transactions for this account</p>
                </div>
              ) : txState === 'loading' ? (
                <div className="py-8 flex justify-center"><LoadingSpinner message={`Loading ${rowCount} transactions...`} /></div>
              ) : (
                <div>
                  {/* This count comes from the UNRECONCILED read, which is `ready`
                      by the time we get here, so it is an observation and stays put
                      even when the transaction detail below fails to arrive. That
                      distinction matters: "3 unreconciled transactions, and we
                      couldn't fetch them" is true and useful; a silent empty table
                      under the same heading is not. */}
                  <p className="text-sm text-gray-700 mb-3">{rowCount} unreconciled transaction{rowCount !== 1 ? 's' : ''}</p>
                  {txState === 'blocked' ? (
                    <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                      <p className="font-medium">Can’t reach the server, so we haven’t loaded these transactions.</p>
                      <p className="mt-1 text-amber-800">
                        This is not an empty transaction list — your connection looks offline.
                        Check it and try again.
                      </p>
                      <button
                        type="button"
                        onClick={() => txQuery.refetch()}
                        className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                      >
                        Try again
                      </button>
                    </div>
                  ) : txState === 'failed' ? (
                    <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                      <p className="font-medium">We couldn’t load these transactions: {txQuery.error?.message}</p>
                      <p className="mt-1">This is not an empty transaction list — nothing was read.</p>
                      <button
                        type="button"
                        onClick={() => txQuery.refetch()}
                        className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                      >
                        Try again
                      </button>
                    </div>
                  ) : txState === 'idle' ? (
                    /* Genuinely reachable, unlike the unreconciled query's `idle`:
                       this one additionally requires `resultId`, and CB can answer
                       the unreconciled call with a rowCount but no result handle. It
                       used to fall straight into `UnreconciledTable` with
                       `data === undefined`, which printed an empty debug dump under
                       a heading promising N transactions. Not an error — the request
                       was correctly never made — so say exactly that. */
                    <p className="text-sm text-gray-500 py-4">
                      The transaction detail hasn’t been requested — the server reported
                      {' '}{rowCount} unreconciled transaction{rowCount !== 1 ? 's' : ''} but returned
                      no result handle to fetch them with.
                    </p>
                  ) : (
                    <UnreconciledTable data={txData?.data} />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-5" role="tabpanel" aria-label="Reconciliation history">
              {historyState === 'loading' ? (
                <div className="py-8 flex justify-center"><LoadingSpinner message="Loading history..." /></div>
              ) : historyState === 'blocked' ? (
                /* "No reconciliation history" against an account that has been
                   reconciled every month for years is a claim about the firm's
                   books, not about the network. It rendered from a read that was
                   never sent. */
                <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                  <p className="font-medium">Can’t reach the server, so we haven’t loaded this account’s history.</p>
                  <p className="mt-1 text-amber-800">
                    This is not an account that has never been reconciled — your connection looks
                    offline, so its history was never read. Check it and try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => historyQuery.refetch()}
                    className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              ) : historyState === 'failed' ? (
                /* Like the unreconciled read, this one's `error` was never
                   destructured, so a thrown history read rendered the empty state
                   verbatim. */
                <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                  <p className="font-medium">We couldn’t load this account’s history: {historyQuery.error?.message}</p>
                  <p className="mt-1">
                    This is not an account that has never been reconciled — nothing was read.
                  </p>
                  <button
                    type="button"
                    onClick={() => historyQuery.refetch()}
                    className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Try again
                  </button>
                </div>
              ) : historyState === 'idle' ? (
                /* Conditionally enabled, so `idle` means "not asked yet" and gets a
                   plain statement rather than an alert or a spinner — same reasoning
                   as the unreconciled panel above, and likewise unreachable only
                   because the surrounding JSX happens to mirror the `enabled`
                   condition. */
                <p className="text-sm text-gray-500 text-center py-4">
                  This account’s reconciliation history hasn’t been requested yet.
                </p>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No reconciliation history</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" role="table">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
                        <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Account</th>
                        <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Reconciled By</th>
                        <th scope="col" className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Statement Bal</th>
                        <th scope="col" className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {history.map((r, i) => (
                        <tr key={r.ReconciliationId || i} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 text-gray-600">{r.StatementDate?.split('T')[0] || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-900">{r.AccountName || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-600">{r.ReconciledByName || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                            ${Number(r.StatementEndingBal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              r.IsSaveForLater ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                            }`}>
                              {r.IsSaveForLater ? 'In Progress' : 'Complete'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const UnreconciledTable = ({ data }) => {
  // The MVC endpoint may return different shapes
  const transactions = Array.isArray(data) ? data
    : (data?.transactions || data?.Transactions || data?.entries || [])

  if (transactions.length === 0) {
    return (
      <div>
        <p className="text-sm text-gray-500 mb-2">Transaction data:</p>
        <pre className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 overflow-x-auto max-h-48">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" role="table">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
            <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
            <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Reference</th>
            <th scope="col" className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Debit</th>
            <th scope="col" className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Credit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {transactions.map((tx, i) => (
            <tr key={tx.Id || tx.TransactionId || i} className="hover:bg-gray-50/50">
              <td className="px-4 py-2.5 text-gray-600">{tx.Date || tx.TransactionDate || tx.DateString || '—'}</td>
              <td className="px-4 py-2.5 text-gray-900">{tx.Type || tx.TransactionType || tx.TypeName || '—'}</td>
              <td className="px-4 py-2.5 text-gray-600">{tx.Reference || tx.Name || tx.RefNum || '—'}</td>
              <td className="px-4 py-2.5 text-right text-gray-900">
                {tx.Debit > 0 || tx.DebitAmount > 0 ? `$${Number(tx.Debit ?? tx.DebitAmount).toFixed(2)}` : ''}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-900">
                {tx.Credit > 0 || tx.CreditAmount > 0 ? `$${Number(tx.Credit ?? tx.CreditAmount).toFixed(2)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default BankReconciliation
